import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
import { authApi } from '@/api/auth'
import { useTheme } from '@/contexts/ThemeContext'

const loginSchema = z.object({
  email: z.string().email('Email non valida'),
  password: z.string().min(1, 'Password richiesta'),
  totp_code: z.string().optional(),
  remember_me: z.boolean().optional(),
})

type LoginForm = z.infer<typeof loginSchema>

// Keys for localStorage
const REMEMBER_ME_KEY = 'login_remember_me'
const SAVED_EMAIL_KEY = 'login_saved_email'
const SAVED_PASSWORD_KEY = 'login_saved_password'

export default function LoginPage() {
  const navigate = useNavigate()
  const { setUser, setTokens } = useAuthStore()
  const { theme } = useTheme()
  const [needs2FA, setNeeds2FA] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null)

  // Initialize from localStorage
  const [rememberMe, setRememberMe] = useState(() => {
    return localStorage.getItem(REMEMBER_ME_KEY) === 'true'
  })

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: localStorage.getItem(SAVED_EMAIL_KEY) || '',
      password: localStorage.getItem(SAVED_PASSWORD_KEY) || '',
    },
  })

  // Restore saved credentials on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem(SAVED_EMAIL_KEY)
    const savedPassword = localStorage.getItem(SAVED_PASSWORD_KEY)
    if (savedEmail) {
      setValue('email', savedEmail)
    }
    if (savedPassword) {
      setValue('password', savedPassword)
    }
  }, [setValue])

  const handleLoginSuccess = (response: { access: string; refresh: string; user: any }, data: LoginForm) => {
    // Save or clear remember me preferences
    if (rememberMe) {
      localStorage.setItem(REMEMBER_ME_KEY, 'true')
      localStorage.setItem(SAVED_EMAIL_KEY, data.email)
      localStorage.setItem(SAVED_PASSWORD_KEY, data.password)
      localStorage.setItem('rememberMe', 'true')
    } else {
      localStorage.removeItem(REMEMBER_ME_KEY)
      localStorage.removeItem(SAVED_EMAIL_KEY)
      localStorage.removeItem(SAVED_PASSWORD_KEY)
      localStorage.removeItem('rememberMe')
      sessionStorage.setItem('sessionAuth', 'true')
    }
    setTokens(response.access, response.refresh)
    setUser(response.user)
    toast.success('Accesso effettuato!')
    navigate('/dashboard')
  }

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      // If 2FA is needed and we have the TOTP code, complete the 2FA login
      if (needs2FA && data.totp_code && pendingCredentials) {
        const response = await authApi.login2FA({
          email: pendingCredentials.email,
          password: pendingCredentials.password,
          token: data.totp_code,
        })
        handleLoginSuccess(response, { ...data, email: pendingCredentials.email, password: pendingCredentials.password })
        return
      }

      // First login attempt
      const response = await authApi.login({ ...data, remember_me: rememberMe })

      if ('requires_2fa' in response && response.requires_2fa) {
        // Store credentials for 2FA completion
        setPendingCredentials({ email: data.email, password: data.password })
        setNeeds2FA(true)
        toast('Inserisci il codice 2FA dalla tua app di autenticazione')
      } else if ('access' in response) {
        handleLoginSuccess(response, data)
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.detail || 'Errore durante il login')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-center" style={{ color: theme.colors.loginText }}>Accedi</h2>
      <p className="mt-2 text-sm text-center" style={{ color: theme.colors.loginText, opacity: 0.7 }}>
        {theme.loginMessage}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6">
        <div>
          <label htmlFor="email" className="label">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            {...register('email')}
            className="input"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="label">
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            {...register('password')}
            className="input"
          />
          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
          )}
        </div>

        {needs2FA && (
          <div>
            <label htmlFor="totp_code" className="label">
              Codice 2FA
            </label>
            <input
              id="totp_code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              {...register('totp_code')}
              className="input"
              placeholder="000000"
            />
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember_me"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="remember_me" className="ml-2 block text-sm text-gray-700">
              Ricordami
            </label>
          </div>
          <div className="text-sm">
            <Link to="/forgot-password" className="text-primary-600 hover:text-primary-500">
              Password dimenticata?
            </Link>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full"
        >
          {isLoading ? 'Accesso in corso...' : 'Accedi'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600">
        Non hai un account?{' '}
        <Link to="/register" className="text-primary-600 hover:text-primary-500 font-medium">
          Registrati
        </Link>
      </p>
    </div>
  )
}
